/**
 * A function to create a log object for the 'purchasedSubscriptionsLoaded' event (evid: 601)
 *
 * Event description: sent when contact purchased subscriptions page loads
 *
 * @param {purchasedSubscriptionsLoadedParams} params - The parameters for this event
 * @property {string} referralInfo - the place click came from
 * @property {GUID} appInstanceId - app instance id
 * @property {number} totalSubscriptions - number of subscriptions as appears to the user
 * @property {GUID} viewedMemberUuid - {APP ONLY} number of ACTIVE subscriptions as appears to the user
 * @property {string} origin - dashboard/editor
 * @property {number} activeSubscriptions - number of ACTIVE subscriptions as appears to the user
 * @return {LogObject<purchasedSubscriptionsLoadedParams>} LogObject<purchasedSubscriptionsLoadedParams>
 */
export function purchasedSubscriptionsLoaded(params) {
    return { evid: 601, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'singleSubscriptionPageLoaded' event (evid: 602)
 *
 * Event description: sent when an individual purchased subscription detail page is loaded
 *
 * @param {singleSubscriptionPageLoadedParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {string} paymentStatus - payment status
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {GUID} viewedMemberUuid - Member viewed
 * @property {string} origin - dashboard/editor
 * @return {LogObject<singleSubscriptionPageLoadedParams>} LogObject<singleSubscriptionPageLoadedParams>
 */
export function singleSubscriptionPageLoaded(params) {
    return { evid: 602, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionCancelClick' event (evid: 603)
 *
 * Event description: sent when a owner clicks on cancel subscription
 *
 * @param {subscriptionCancelClickParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {string} paymentStatus - payment status
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {string} origin - dashboard/editor
 * @return {LogObject<subscriptionCancelClickParams>} LogObject<subscriptionCancelClickParams>
 */
export function subscriptionCancelClick(params) {
    return { evid: 603, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionCancelled' event (evid: 604)
 *
 * Event description: sent when subscription cancellation is confirmed (auto renew off or cancel immediately)
 *
 * @param {subscriptionCancelledParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {string} expirationTime - expiration time of a subscription (now/period end)
 * @property {string} paymentStatus - payment status
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {string} cancelationType - cancel immediately/auto renew off
 * @property {string} origin - dashboard/editor
 * @return {LogObject<subscriptionCancelledParams>} LogObject<subscriptionCancelledParams>
 */
export function subscriptionCancelled(params) {
    return { evid: 604, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'mySubscriptionsActionItemClick' event (evid: 605)
 *
 * Event description: sent when action item is clicked in My Subscription page
 *
 * @param {mySubscriptionsActionItemClickParams} params - The parameters for this event
 * @property {GUID} sessionid - session in my subscriptions page level (same id for all actions in the page until refresh/cancel)
 * @property {GUID} planGuid - plan guid
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {boolean} emptyState - indicates if action item was clicked in empty state (no purchased Subscriptions)
 * @property {string} action - name of the action {cancel, buy_new_plan, reactivate},
 * @return {LogObject<mySubscriptionsActionItemClickParams>} LogObject<mySubscriptionsActionItemClickParams>
 */
export function mySubscriptionsActionItemClick(params) {
    return { evid: 605, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'mySubscriptionsShowDetails' event (evid: 606)
 *
 * Event description: sent when Subscription drawer with details is expanded
 *
 * @param {mySubscriptionsShowDetailsParams} params - The parameters for this event
 * @property {string} sessionid - session
 * @property {GUID} planGuid - plan guid
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {string} subscriptionStatus - status of the subscription at the time of action
 * @property {string} assetVersion - indication - Cairo or old
 * @return {LogObject<mySubscriptionsShowDetailsParams>} LogObject<mySubscriptionsShowDetailsParams>
 */
export function mySubscriptionsShowDetails(params) {
    return { evid: 606, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'mySubscriptionsCancelSubscriptionConfirmed' event (evid: 607)
 *
 * Event description: sent when subscription cancellation is confirmed in Modal
 *
 * @param {mySubscriptionsCancelSubscriptionConfirmedParams} params - The parameters for this event
 * @property {GUID} sessionid - session in my subscriptions page level (same id for all actions in the page until refresh/cancel)
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {GUID} originEntityId - plan_guid, product_id or invoice_id
 * @property {GUID} appId - vertical id (stores, paid plans, invoices)
 * @property {string} action - value of the cancel action {cancel} for initiate, {cancel-confirm} for confirmation in modal
 * @property {string} assetVersion - indication - Cairo or old
 * @return {LogObject<mySubscriptionsCancelSubscriptionConfirmedParams>} LogObject<mySubscriptionsCancelSubscriptionConfirmedParams>
 */
export function mySubscriptionsCancelSubscriptionConfirmed(params) {
    return { evid: 607, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'manageSubscriptionClick' event (evid: 608)
 *
 * Event description: sent when a User clicks on a manage subscription CTA
 *
 * @param {manageSubscriptionClickParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {string} paymentStatus - payment status
 * @property {GUID} subscriptionId - subscription id
 * @property {string} disabledActions - disabled actions and the reasons for it
 * @property {string} appinstanceid - app id
 * @property {string} origin - dashboard/editor
 * @return {LogObject<manageSubscriptionClickParams>} LogObject<manageSubscriptionClickParams>
 */
export function manageSubscriptionClick(params) {
    return { evid: 608, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionSuspendedOrReactivated' event (evid: 609)
 *
 * Event description: sent when a subscription is suspended or reactivated
 *
 * @param {subscriptionSuspendedOrReactivatedParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {string} origin - dashboard/editor
 * @property {string} action - 'suspend', 'suspend-confirm', 'reactivate', 'reactivate-confirm'
 * @return {LogObject<subscriptionSuspendedOrReactivatedParams>} LogObject<subscriptionSuspendedOrReactivatedParams>
 */
export function subscriptionSuspendedOrReactivated(params) {
    return { evid: 609, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionListFilterApplied' event (evid: 610)
 *
 * Event description: sent when list of subscriptions is filtered
 *
 * @param {subscriptionListFilterAppliedParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {GUID} appInstanceId - app id
 * @property {number} totalSubscriptions - number of subscriptions as appears to the user. From  widget (at OneApp) we can't have the number of subs since it is always up to 3 there
 * @property {string} filter - vertical/status
 * @property {string} origin - dashboard/editor
 * @return {LogObject<subscriptionListFilterAppliedParams>} LogObject<subscriptionListFilterAppliedParams>
 */
export function subscriptionListFilterApplied(params) {
    return { evid: 610, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionNumberOfSessionsChanged' event (evid: 612)
 *
 * Event description: sent when number of sessions is edited for a subscription
 *
 * @param {subscriptionNumberOfSessionsChangedParams} params - The parameters for this event
 * @property {number} originalSessions - number of sessions originally offered
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription ID
 * @property {number} newSessions - new number of sessions offered
 * @property {string} origin - dashboard/editor
 * @property {string} action - {intent/confirm}
 * @return {LogObject<subscriptionNumberOfSessionsChangedParams>} LogObject<subscriptionNumberOfSessionsChangedParams>
 */
export function subscriptionNumberOfSessionsChanged(params) {
    return { evid: 612, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionDurationChanged' event (evid: 614)
 *
 * Event description: sent when duration is changed for a subscription (e.g. duration extended)
 *
 * @param {subscriptionDurationChangedParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {number} daysAdded - number of days added to duration
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription Id
 * @property {string} origin - dashboard/editor
 * @property {string} action - {intent/confirm}
 * @return {LogObject<subscriptionDurationChangedParams>} LogObject<subscriptionDurationChangedParams>
 */
export function subscriptionDurationChanged(params) {
    return { evid: 614, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionMarkedAsPaid' event (evid: 615)
 *
 * Event description: sent when subscription is marked as paid
 *
 * @param {subscriptionMarkedAsPaidParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {GUID} appInstanceId - app id
 * @property {GUID} subscriptionId - subscription id
 * @property {string} origin - dashboard/editor
 * @property {string} action - {intent/confirm}
 * @return {LogObject<subscriptionMarkedAsPaidParams>} LogObject<subscriptionMarkedAsPaidParams>
 */
export function subscriptionMarkedAsPaid(params) {
    return { evid: 615, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'mySubscriptionsOpened' event (evid: 616)
 *
 * Event description: sent when My Subscriptions page is opened
 *
 * @param {mySubscriptionsOpenedParams} params - The parameters for this event
 * @property {string} referralInfo - page/widget/cta which referred to My Subscriptions. E.g. {login-menu, ma-menu}
 * @property {string} sessionid - session
 * @property {number} totalSubscriptions - number of subscriptions as appears to the user
 * @property {string} tabName - on app - indicate which tab is seen{active,expired}
 * @property {string} origin - Site - UoU view My Subscriptions page
 * my-subscriptions - UoU view My Subscriptions page from My Account tab in one of the client apps
 * home-widget - UoU view Pricing Plans Subscriptions (aka My Memberships) from Home tab on Fit app
 * Preview - user view My Subscriptions page from editor
 * @return {LogObject<mySubscriptionsOpenedParams>} LogObject<mySubscriptionsOpenedParams>
 */
export function mySubscriptionsOpened(params) {
    return { evid: 616, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionsMiscClick' event (evid: 617)
 *
 * Event description: Buttons/links/etc clicks at random places.
 *
 * @param {subscriptionsMiscClickParams} params - The parameters for this event
 * @property {string} referralInfo - identifier of a button, link
 * @return {LogObject<subscriptionsMiscClickParams>} LogObject<subscriptionsMiscClickParams>
 */
export function subscriptionsMiscClick(params) {
    return { evid: 617, src: 92, endpoint: 'subscriptions-bm', params: params };
}
/**
 * A function to create a log object for the 'subscriptionInvoicesAction' event (evid: 618)
 *
 * Event description: Invoices actions at Single Subscription
 *
 * @param {subscriptionInvoicesActionParams} params - The parameters for this event
 * @property {GUID} subscriptionId - subscription_id
 * @property {GUID} invoiceId - invoice_id
 * @property {string} action - preview, ...
 * @return {LogObject<subscriptionInvoicesActionParams>} LogObject<subscriptionInvoicesActionParams>
 */
export function subscriptionInvoicesAction(params) {
    return { evid: 618, src: 92, endpoint: 'subscriptions-bm', params: params };
}
//# sourceMappingURL=index.js.map